/**
 * Captures `beforeinstallprompt` as early as possible (before React mounts).
 * Chromium often fires this once per page load, and on later reloads it can fire
 * before any React listener is attached — so we stash it on window.
 */

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const PROMPT_KEY = "__HOPE_PWA_DEFERRED_PROMPT__";
const INSTALLED_KEY = "thehopeone_pwa_installed";

type PwaWindow = Window & {
  [PROMPT_KEY]?: BeforeInstallPromptEvent | null;
};

const getWin = (): PwaWindow | null =>
  typeof window === "undefined" ? null : (window as PwaWindow);

export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return getWin()?.[PROMPT_KEY] ?? null;
}

export function setDeferredInstallPrompt(prompt: BeforeInstallPromptEvent | null): void {
  const win = getWin();
  if (win) win[PROMPT_KEY] = prompt;
}

export function clearDeferredInstallPrompt(): void {
  setDeferredInstallPrompt(null);
}

export function markPwaInstalled(): void {
  try {
    localStorage.setItem(INSTALLED_KEY, "1");
  } catch {
    // ignore
  }
  clearDeferredInstallPrompt();
}

export function wasPwaInstallMarked(): boolean {
  try {
    return localStorage.getItem(INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearPwaInstallMarked(): void {
  try {
    localStorage.removeItem(INSTALLED_KEY);
  } catch {
    // ignore
  }
}

export function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  const mediaStandalone = window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone =
    "standalone" in window.navigator &&
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  return mediaStandalone || iosStandalone;
}

/** Call once at app bootstrap, before React render. */
export function bindPwaInstallBridge(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    setDeferredInstallPrompt(event as BeforeInstallPromptEvent);
    // If the browser is offering install again, clear a stale "installed" flag.
    clearPwaInstallMarked();
    window.dispatchEvent(new Event("hope:pwa-prompt-available"));
  });

  window.addEventListener("appinstalled", () => {
    markPwaInstalled();
    window.dispatchEvent(new Event("hope:pwa-installed"));
  });
}
